import React, { useEffect, useCallback, useRef } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Shared building blocks for the photo gallery (mosaic grid and lightbox).
 * Not a registered component — has no `contentSchema`, never appears in
 * `registry.ts`, and is not re-exported from `components/index.ts`.
 *
 * The schema linter skips files without a contentSchema, so this helper
 * sits safely alongside the registered gallery sections without tripping
 * any rules (every photo is rendered through VibesitesImage).
 */

export interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

/** Classify a gallery photo by its title — used to split community vs unit shots. */
export function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

const MOSAIC_SIZE = 5;

function GalleryTile({
  img,
  index,
  onSelect,
  className,
  loading = 'lazy',
}: {
  img: GalleryImage;
  index: number;
  onSelect: (index: number) => void;
  className?: string;
  loading?: 'lazy' | 'eager';
}) {
  return (
    <div
      className={`gallery-item group cursor-pointer overflow-hidden ${className || ''}`}
      onClick={() => onSelect(index)}
    >
      <VibesitesImage
        url={img.image}
        alt={img.alt_text || img.title || ''}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
        priority={loading === 'eager'}
      />
    </div>
  );
}

interface GalleryMosaicBlockProps {
  images: GalleryImage[];
  startIndex: number;
  tallOnRight: boolean;
  onSelect: (index: number) => void;
}

/** Five-up mosaic: 2×2 grid beside one tall portrait image. */
export function GalleryMosaicBlock({
  images,
  startIndex,
  tallOnRight,
  onSelect,
}: GalleryMosaicBlockProps) {
  if (images.length === 0) return null;

  if (images.length < MOSAIC_SIZE) {
    return (
      <div className="gallery-fallback-grid">
        {images.map((img, i) => (
          <GalleryTile
            key={img.image || startIndex + i}
            img={img}
            index={startIndex + i}
            onSelect={onSelect}
            className="gallery-fallback-grid__cell"
          />
        ))}
      </div>
    );
  }

  const quad = images.slice(0, 4);
  const tall = images[4];
  const modifier = tallOnRight ? 'gallery-mosaic--tall-right' : 'gallery-mosaic--tall-left';

  return (
    <div className={`gallery-mosaic ${modifier}`}>
      {quad.map((img, i) => (
        <GalleryTile
          key={img.image || startIndex + i}
          img={img}
          index={startIndex + i}
          onSelect={onSelect}
          className="gallery-mosaic__cell"
          loading={i === 0 && startIndex === 0 ? 'eager' : 'lazy'}
        />
      ))}
      <GalleryTile
        img={tall}
        index={startIndex + 4}
        onSelect={onSelect}
        className="gallery-mosaic__tall"
      />
    </div>
  );
}

/** Split a flat image list into repeating 5-up mosaic groups. */
export function chunkGalleryImages(images: GalleryImage[], size = MOSAIC_SIZE): GalleryImage[][] {
  const chunks: GalleryImage[][] = [];
  for (let i = 0; i < images.length; i += size) {
    chunks.push(images.slice(i, i + size));
  }
  return chunks;
}

interface GalleryLightboxProps {
  images: GalleryImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}

function LightboxChevron({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === 'prev' ? (
        <path d="M15 18l-6-6 6-6" />
      ) : (
        <path d="M9 18l6-6-6-6" />
      )}
    </svg>
  );
}

export function GalleryLightbox({
  images,
  selectedIndex,
  onClose,
  onChange,
}: GalleryLightboxProps) {
  const total = images.length;

  const goNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex - 1 + total) % total);
    },
    [onChange, selectedIndex, total],
  );

  useEffect(() => {
    if (selectedIndex === null) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, goNext, goPrev, onClose]);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;

      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  const handleStageClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const stopClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  if (!selectedImage) return null;

  const caption = selectedImage.title || selectedImage.alt_text || '';

  return (
    <div
      className="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery viewer"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="gallery-lightbox__shell">
        <div className="gallery-lightbox__toolbar">
          {total > 1 ? (
            <p className="gallery-lightbox__counter" aria-live="polite">
              {(selectedIndex ?? 0) + 1} <span className="gallery-lightbox__counter-sep">/</span>{' '}
              {total}
            </p>
          ) : (
            <span aria-hidden="true" />
          )}

          <button
            type="button"
            onClick={onClose}
            className="gallery-lightbox__close"
            aria-label="Close gallery"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          className="gallery-lightbox__stage"
          onClick={handleStageClick}
        >
          {total > 1 && (
            <button
              type="button"
              onClick={goPrev}
              className="gallery-lightbox__nav gallery-lightbox__nav--prev gallery-lightbox__nav--desktop"
              aria-label="Previous photo"
            >
              <LightboxChevron direction="prev" />
            </button>
          )}

          <figure className="gallery-lightbox__figure">
            <div className="gallery-lightbox__img-wrap">
              <div className="gallery-lightbox__img-frame" onClick={stopClick}>
                <VibesitesImage
                  url={selectedImage.image}
                  alt={selectedImage.alt_text || selectedImage.title || 'Property photo'}
                  className="gallery-lightbox__img"
                />
              </div>
            </div>
            {caption ? (
              <figcaption className="gallery-lightbox__caption" onClick={stopClick}>
                {caption}
              </figcaption>
            ) : null}
          </figure>

          {total > 1 && (
            <button
              type="button"
              onClick={goNext}
              className="gallery-lightbox__nav gallery-lightbox__nav--next gallery-lightbox__nav--desktop"
              aria-label="Next photo"
            >
              <LightboxChevron direction="next" />
            </button>
          )}
        </div>

        {total > 1 && (
          <div className="gallery-lightbox__mobile-nav">
            <button
              type="button"
              onClick={goPrev}
              className="gallery-lightbox__nav gallery-lightbox__nav--prev gallery-lightbox__nav--mobile"
              aria-label="Previous photo"
            >
              <LightboxChevron direction="prev" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="gallery-lightbox__nav gallery-lightbox__nav--next gallery-lightbox__nav--mobile"
              aria-label="Next photo"
            >
              <LightboxChevron direction="next" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Shared CSS for the gallery mosaic and lightbox.
 */
export function GallerySharedStyles() {
  return (
    <style>{`
      .gallery-constrained {
        width: 90vw;
        max-width: 90vw;
        margin-left: auto;
        margin-right: auto;
        padding-left: 0;
        padding-right: 0;
      }

      @media (max-width: 767px) {
        .gallery-constrained {
          width: 100%;
          max-width: 100%;
        }

        .gallery-mosaic-stack {
          gap: 0.375rem;
        }

        .gallery-mosaic {
          gap: 0.375rem;
        }

        .gallery-fallback-grid {
          gap: 0.375rem;
        }
      }

      .gallery-item {
        animation: gallery-fade-in 0.35s ease both;
      }

      @keyframes gallery-fade-in {
        from { opacity: 0; transform: scale(0.97); }
        to { opacity: 1; transform: scale(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-item { animation: none; }
      }

      /* ── Mosaic (mobile: 2×2 + square; desktop: 2×2 + tall) ─ */
      .gallery-mosaic-stack {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .gallery-mosaic {
        display: grid;
        gap: 0.75rem;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto auto auto;
      }

      .gallery-mosaic__cell {
        aspect-ratio: 1 / 1;
      }

      .gallery-mosaic__cell:nth-child(1) { grid-column: 1; grid-row: 1; }
      .gallery-mosaic__cell:nth-child(2) { grid-column: 2; grid-row: 1; }
      .gallery-mosaic__cell:nth-child(3) { grid-column: 1; grid-row: 2; }
      .gallery-mosaic__cell:nth-child(4) { grid-column: 2; grid-row: 2; }

      .gallery-mosaic__tall {
        grid-column: 1 / -1;
        grid-row: 3;
        aspect-ratio: 1 / 1;
      }

      @media (min-width: 768px) {
        .gallery-mosaic {
          grid-template-rows: auto auto;
        }

        .gallery-mosaic--tall-right {
          grid-template-columns: 1fr 1fr minmax(0, 1fr);
        }

        .gallery-mosaic--tall-left {
          grid-template-columns: minmax(0, 1fr) 1fr 1fr;
        }

        .gallery-mosaic__cell {
          aspect-ratio: 4 / 3;
        }

        .gallery-mosaic__tall {
          grid-row: 1 / 3;
          aspect-ratio: auto;
        }

        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(1) { grid-column: 1; grid-row: 1; }
        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(2) { grid-column: 2; grid-row: 1; }
        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(3) { grid-column: 1; grid-row: 2; }
        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(4) { grid-column: 2; grid-row: 2; }
        .gallery-mosaic--tall-right .gallery-mosaic__tall { grid-column: 3; grid-row: 1 / 3; }

        .gallery-mosaic--tall-left .gallery-mosaic__tall { grid-column: 1; grid-row: 1 / 3; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(1) { grid-column: 2; grid-row: 1; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(2) { grid-column: 3; grid-row: 1; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(3) { grid-column: 2; grid-row: 2; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(4) { grid-column: 3; grid-row: 2; }
      }

      .gallery-fallback-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
      }

      .gallery-fallback-grid__cell {
        aspect-ratio: 1 / 1;
      }

      @media (min-width: 768px) {
        .gallery-fallback-grid__cell {
          aspect-ratio: 4 / 3;
        }
      }

      /* ── Lightbox ───────────────────────────────────────────── */
      .gallery-lightbox {
        position: fixed;
        inset: 0;
        z-index: 50;
        display: flex;
        align-items: stretch;
        justify-content: center;
        background: rgba(0, 0, 0, 0.94);
        animation: gallery-lightbox-in 0.2s ease both;
      }

      @keyframes gallery-lightbox-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-lightbox { animation: none; }
      }

      .gallery-lightbox__shell {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 100vw;
        height: 100dvh;
      }

      .gallery-lightbox__toolbar,
      .gallery-lightbox__stage,
      .gallery-lightbox__mobile-nav,
      .gallery-lightbox__nav,
      .gallery-lightbox__close,
      .gallery-lightbox__img-frame,
      .gallery-lightbox__caption {
        pointer-events: auto;
      }

      .gallery-lightbox__toolbar {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.875rem 1rem 0.5rem;
      }

      .gallery-lightbox__counter {
        margin: 0;
        font-family: var(--vibe-font-body);
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: 0.06em;
        color: rgba(255, 255, 255, 0.88);
      }

      .gallery-lightbox__counter-sep {
        color: rgba(255, 255, 255, 0.45);
      }

      .gallery-lightbox__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        border: none;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        cursor: pointer;
        transition: background 0.15s ease, transform 0.15s ease;
      }

      .gallery-lightbox__close:hover {
        background: rgba(255, 255, 255, 0.18);
      }

      .gallery-lightbox__close:active {
        transform: scale(0.96);
      }

      .gallery-lightbox__stage {
        flex: 1;
        min-height: 0;
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem 1rem;
        cursor: pointer;
        touch-action: pan-y;
      }

      .gallery-lightbox__figure {
        margin: 0;
        min-width: 0;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        pointer-events: none;
      }

      .gallery-lightbox__img-wrap {
        flex: 1;
        min-height: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }

      .gallery-lightbox__img-frame {
        display: inline-flex;
        max-width: 100%;
        max-height: 100%;
        cursor: default;
      }

      .gallery-lightbox__img {
        display: block;
        max-width: min(96vw, 1400px);
        max-height: calc(100dvh - 5.5rem);
        width: auto;
        height: auto;
        object-fit: contain;
        border-radius: 2px;
        box-shadow: 0 12px 48px rgba(0, 0, 0, 0.45);
      }

      .gallery-lightbox__caption {
        flex-shrink: 0;
        max-width: min(96vw, 1400px);
        margin: 0;
        padding: 0 0.5rem;
        font-family: var(--vibe-font-body);
        font-size: 0.8125rem;
        line-height: 1.4;
        letter-spacing: 0.02em;
        text-align: center;
        color: rgba(255, 255, 255, 0.72);
      }

      .gallery-lightbox__nav {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
        cursor: pointer;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
        -webkit-user-select: none;
        user-select: none;
      }

      .gallery-lightbox__nav:hover {
        background: rgba(255, 255, 255, 0.16);
        border-color: rgba(255, 255, 255, 0.28);
      }

      .gallery-lightbox__nav:active {
        transform: scale(0.96);
      }

      .gallery-lightbox__mobile-nav {
        display: none;
      }

      .gallery-lightbox__nav--desktop {
        cursor: pointer;
      }

      @media (min-width: 768px) {
        .gallery-lightbox__toolbar {
          padding: 1rem 1.25rem 0.625rem;
        }

        .gallery-lightbox__stage {
          gap: 1rem;
          padding: 0 1rem 1.25rem;
        }

        .gallery-lightbox__nav {
          width: 3rem;
          height: 3rem;
        }

        .gallery-lightbox__img {
          max-height: calc(100dvh - 6rem);
        }

        .gallery-lightbox__caption {
          font-size: 0.875rem;
        }
      }

      @media (max-width: 639px) {
        .gallery-lightbox__stage {
          grid-template-columns: minmax(0, 1fr);
          grid-template-rows: 1fr;
          gap: 0;
          padding: 0;
        }

        .gallery-lightbox__figure {
          width: 100%;
          padding: 0;
          align-items: center;
          justify-content: center;
        }

        .gallery-lightbox__img-wrap {
          width: 100%;
          align-items: center;
          justify-content: center;
        }

        .gallery-lightbox__img-frame {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-height: 100%;
        }

        .gallery-lightbox__nav--desktop {
          display: none;
        }

        .gallery-lightbox__mobile-nav {
          display: flex;
          flex-shrink: 0;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.5rem 1.25rem calc(0.875rem + env(safe-area-inset-bottom, 0px));
        }

        .gallery-lightbox__nav--mobile {
          width: 3.25rem;
          height: 3.25rem;
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.16);
        }

        .gallery-lightbox__nav--mobile:active {
          transform: scale(0.96);
        }

        .gallery-lightbox__img {
          display: block;
          width: 100%;
          max-width: 100%;
          height: auto;
          max-height: calc(100dvh - 8.5rem);
          margin: 0 auto;
          border-radius: 0;
          box-shadow: none;
          object-fit: contain;
        }

        .gallery-lightbox__caption {
          padding: 0 1rem 0.25rem;
          max-width: 100%;
        }
      }
    `}</style>
  );
}
